/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TNestedValue } from '@ben/shared/components/benefit-sections/multi-select-options/multi-select-options';
import {
  isComponentHiddenForAudience,
  type ModularBenefitAudience,
} from '@ben/shared/utils/modular-benefit-audience-visibility';
import { camelCase, mapKeys } from 'lodash';
import { memo, useMemo } from 'react';
import styled from 'styled-components';
import type { ContactOption, ContactSelection } from '../use-contact-selection';
import ContactCardHeader from './contact-card-header';
import ContactCardSubComponents from './contact-card-sub-components';

interface ContactCardProps {
  contact: ContactOption;
  disabled?: boolean;
  benefitId: string | number;
  contactSubComponentValues?: Record<string, any>[];
  onChildValueChange?: (
    parentKey: string,
    key: string,
    value: TNestedValue
  ) => void;
  originalComponentValues?: ContactSelection[];
  onRemove: (value: string) => void;
  requiredContactFields: string[];
  backendErrors?: Record<string, unknown>;
  audience?: ModularBenefitAudience;
}

export const ContactCard = ({
  contact,
  disabled,
  contactSubComponentValues,
  onChildValueChange,
  benefitId,
  originalComponentValues,
  onRemove,
  requiredContactFields,
  backendErrors,
  audience = 'employee',
}: ContactCardProps) => {
  const hasUnsavedChanges = useMemo(() => {
    const originalSelection = originalComponentValues?.find(
      (selection) => String(selection.id) === contact.key
    );

    if (!originalSelection) {
      return true;
    }

    const currentSubComponentValues = contactSubComponentValues?.find(
      (subValue) => Object.keys(subValue).includes(contact.key)
    )?.[contact.key];

    if (!currentSubComponentValues) {
      return false;
    }

    return Object.entries(currentSubComponentValues).some(
      ([key, value]) => originalSelection.values?.[key] !== value
    );
  }, [originalComponentValues, contactSubComponentValues, contact.key]);

  const currentSubComponentValues = contactSubComponentValues?.find(
    (subValue) => Object.keys(subValue).includes(contact.key)
  )?.[contact.key];

  const camelCaseValues = currentSubComponentValues
    ? mapKeys(currentSubComponentValues, (_, key) => camelCase(key))
    : {};

  const hasMissingRequiredFields = !!contact.subComponents?.some(
    (subComponent) =>
      !isComponentHiddenForAudience(subComponent, audience) &&
      subComponent.required &&
      !camelCaseValues[camelCase(subComponent.key)]
  );

  return (
    <Wrapper>
      <ContactCardHeader
        contact={contact}
        disabled={disabled}
        hasMissingRequiredFields={hasMissingRequiredFields}
        hasUnsavedChanges={hasUnsavedChanges}
        onRemove={onRemove}
        requiredContactFields={requiredContactFields}
      />
      <ContactCardSubComponents
        contact={contact}
        benefitId={benefitId}
        disabled={disabled}
        contactSubComponentValues={contactSubComponentValues}
        onChildValueChange={onChildValueChange}
        backendErrors={backendErrors}
        showRequiredErrors={hasMissingRequiredFields}
        audience={audience}
      />
    </Wrapper>
  );
};

export default memo(ContactCard);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
